import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import SbEditable from 'storyblok-react';

//

const defaultFields = [
  { name: 'firstName', label: 'First name', type: 'text', required: true },
  { name: 'lastName', label: 'Last name', type: 'text', required: true },
  { name: 'email', label: 'Email', type: 'email', required: true },
  { name: 'company', label: 'Company', type: 'text', required: false },
  { name: 'message', label: 'Message', type: 'textarea', required: false },
];

const getFields = (content) =>
  Array.isArray(content.Fields) && content.Fields.length
    ? content.Fields
    : defaultFields;

const FloatingFormButton = ({ content }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState('idle');
  const fields = getFields(content);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!content.SubmitURL) {
      setStatus('missing-url');
      return;
    }

    const formData = new FormData(event.currentTarget);
    const payload = {};

    formData.forEach((value, key) => {
      payload[key] = value;
    });

    setStatus('submitting');

    try {
      const response = await fetch(content.SubmitURL, {
        method: content.Method || 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Form submit failed');

      event.currentTarget.reset();
      setStatus('success');
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <SbEditable content={content}>
      <FloatingWrap position={content.Position}>
        {isOpen && (
          <Panel>
            <PanelHeader>
              <h3>{content.FormTitle || 'Contact us'}</h3>
              <CloseButton
                type="button"
                aria-label="Close form"
                onClick={() => setIsOpen(false)}
              >
                x
              </CloseButton>
            </PanelHeader>

            {content.FormIntro && <Intro>{content.FormIntro}</Intro>}

            <Form onSubmit={handleSubmit}>
              {fields.map((field) => (
                <Field key={field.name}>
                  <label htmlFor={field.name}>{field.label}</label>
                  {field.type === 'textarea' ? (
                    <textarea
                      id={field.name}
                      name={field.name}
                      required={field.required}
                    />
                  ) : (
                    <input
                      id={field.name}
                      name={field.name}
                      type={field.type || 'text'}
                      required={field.required}
                    />
                  )}
                </Field>
              ))}

              <SubmitButton type="submit" disabled={status === 'submitting'}>
                {status === 'submitting'
                  ? content.SubmittingLabel || 'Submitting...'
                  : content.SubmitLabel || 'Submit'}
              </SubmitButton>
            </Form>

            {status === 'success' && (
              <Message>{content.SuccessMessage || 'Thank you.'}</Message>
            )}
            {status === 'error' && (
              <Message role="alert">
                {content.ErrorMessage || 'Something went wrong.'}
              </Message>
            )}
            {status === 'missing-url' && (
              <Message role="alert">Missing form submit URL.</Message>
            )}
          </Panel>
        )}

        <TriggerButton type="button" onClick={() => setIsOpen(!isOpen)}>
          {isOpen
            ? content.CloseLabel || 'Close'
            : content.ButtonLabel || 'Contact us'}
        </TriggerButton>
      </FloatingWrap>
    </SbEditable>
  );
};

export default FloatingFormButton;

const FloatingWrap = styled.div`
  position: fixed;
  right: ${(props) => (props.position === 'bottom-left' ? 'auto' : '3rem')};
  bottom: 3rem;
  left: ${(props) => (props.position === 'bottom-left' ? '3rem' : 'auto')};
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2.4rem;

  @media (max-width: 640px) {
    right: 1.6rem;
    bottom: 1.6rem;
    left: 1.6rem;
    align-items: stretch;
  }
`;

const Panel = styled.div`
  width: min(33rem, calc(100vw - 3.2rem));
  padding: 3.2rem 2.8rem 2.8rem;
  border: 0;
  border-radius: 0.8rem;
  background: #fff;
  box-shadow: 0 1.2rem 3.2rem rgba(0, 0, 0, 0.22);
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.6rem;

  h3 {
    margin: 0;
    color: #181818;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 2.6rem;
    font-weight: 500;
    line-height: 3.2rem;
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.8rem;
  height: 2.8rem;
  margin: -1.6rem -1.2rem 0 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #b9b9b9;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
`;

const Intro = styled.p`
  margin: 1.2rem 0 0;
  color: #747474;
  font-size: 1.4rem;
  line-height: 2rem;
`;

const Form = styled.form`
  display: grid;
  gap: 1.4rem;
  margin-top: 2.8rem;
`;

const Field = styled.div`
  display: grid;
  gap: 0.8rem;

  label {
    color: #1b1b1b;
    font-size: 1.2rem;
    font-weight: 700;
    line-height: 1.6rem;
  }

  input,
  textarea {
    width: 100%;
    border: 1px solid #e2e4e8;
    border-radius: 0.2rem;
    padding: 1.1rem 1.2rem;
    background: #f9fafb;
    color: #222;
    font: inherit;
    font-size: 1.3rem;
    line-height: 1.8rem;
    outline-color: #f6aa16;
  }

  textarea {
    min-height: 9.6rem;
    resize: vertical;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  margin-top: 2.8rem;
  border: 0;
  border-radius: 0.2rem;
  padding: 1.3rem 1.6rem;
  background: #ffad13;
  color: #fff;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.6rem;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0 0.4rem 0.8rem rgba(0, 0, 0, 0.18);

  &:disabled {
    cursor: wait;
    opacity: 0.7;
  }
`;

const TriggerButton = styled.button`
  align-self: flex-end;
  min-width: 14.4rem;
  border: 0;
  border-radius: 999rem;
  padding: 1.4rem 2rem;
  background: #db961c;
  color: #fff;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.6rem;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0 0.6rem 1.2rem rgba(0, 0, 0, 0.26);

  @media (max-width: 640px) {
    align-self: stretch;
  }
`;

const Message = styled.p`
  margin: 1.2rem 0 0;
  color: #747474;
  font-size: 1.3rem;
  line-height: 1.8rem;
`;

FloatingFormButton.propTypes = {
  content: PropTypes.shape({
    ButtonLabel: PropTypes.string,
    CloseLabel: PropTypes.string,
    ErrorMessage: PropTypes.string,
    Fields: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        name: PropTypes.string,
        required: PropTypes.bool,
        type: PropTypes.string,
      })
    ),
    FormIntro: PropTypes.string,
    FormTitle: PropTypes.string,
    Method: PropTypes.string,
    Position: PropTypes.string,
    SubmitLabel: PropTypes.string,
    SubmitURL: PropTypes.string,
    SubmittingLabel: PropTypes.string,
    SuccessMessage: PropTypes.string,
  }),
};

FloatingFormButton.defaultProps = {
  content: {
    ButtonLabel: 'Contact us',
    CloseLabel: 'Close',
    ErrorMessage: 'Something went wrong.',
    Fields: defaultFields,
    FormIntro: null,
    FormTitle: 'Contact us',
    Method: 'POST',
    Position: 'bottom-right',
    SubmitLabel: 'Submit',
    SubmitURL: null,
    SubmittingLabel: 'Submitting...',
    SuccessMessage: 'Thank you.',
  },
};
